/**
 * Blockchain API Service
 * Uses Blockstream.info API for address lookups
 * Supports both mainnet and testnet
 * Can optionally use local Bitcoin node if configured
 */

export interface AddressInfo {
  address: string
  balance: number // in satoshis
  totalReceived: number // in satoshis
  totalSent: number // in satoshis
  txCount: number
  sentTxCount?: number // Number of transactions where address was input (sent)
  receivedTxCount?: number // Number of transactions where address was output (received)
  hasTransactions: boolean
  hasSpent: boolean // true if address has ever sent funds (exposed public key)
  unconfirmedBalance: number
  network: 'mainnet' | 'testnet' | 'regtest'
  isP2PK?: boolean // true if any UTXOs are P2PK type
}

export interface QD3RegistrationStatus {
  isRegistered: boolean
  registrationTxId?: string
  registrationDate?: Date
  qd3Version?: number
  algorithm?: string
  publicKeyHash?: string
  signatureHash?: string
  rawData?: string
  /**
   * True only when the address's entire transaction history was scanned without
   * finding a QD3 record. False when the scan stopped early — a request failed,
   * or the page cap was reached — in which case `isRegistered: false` means
   * "not found so far", NOT "not registered". Callers must not report an
   * address as unregistered on an incomplete scan.
   */
  scanComplete?: boolean
  /** Number of transactions examined, for surfacing the limit to the user. */
  txsScanned?: number
}

export interface UTXO {
  txid: string
  vout: number
  value: number
  status: {
    confirmed: boolean
    block_height?: number
    block_hash?: string
    block_time?: number
  }
}

export class BlockchainAPI {
  private static instance: BlockchainAPI
  private baseUrlMainnet = 'https://blockstream.info/api'
  private baseUrlTestnet = 'https://blockstream.info/testnet/api'

  /**
   * Pages of history the QD3 scan will walk before giving up (25 txs per page,
   * so 40 pages ≈ 1,000 transactions). The cap bounds the request count on very
   * busy addresses; hitting it yields `scanComplete: false`, never a false
   * "not registered".
   */
  private static readonly QD3_SCAN_MAX_PAGES = 40
  
  static getInstance(): BlockchainAPI {
    if (!BlockchainAPI.instance) {
      BlockchainAPI.instance = new BlockchainAPI()
    }
    return BlockchainAPI.instance
  }

  /**
   * Detect network from address
   */
  private detectNetwork(address: string): 'mainnet' | 'testnet' | 'regtest' {
    // Testnet detection
    if (address.startsWith('m') || address.startsWith('n') || // P2PKH testnet
        address.startsWith('2') || // P2SH testnet  
        address.startsWith('tb1')) { // Bech32 testnet
      return 'testnet'
    }
    // Regtest detection
    else if (address.startsWith('bcrt1')) {
      return 'regtest'
    }
    // Default to mainnet
    return 'mainnet'
  }

  /**
   * Get base URL for the network
   */
  private getBaseUrl(network: 'mainnet' | 'testnet' | 'regtest'): string {
    if (network === 'testnet') {
      return this.baseUrlTestnet
    }
    // Note: Blockstream doesn't have a public regtest API
    // For regtest, we'd need a local node
    return this.baseUrlMainnet
  }

  /**
   * Check if a scriptPubKey is P2PK format
   * P2PK format: <pubkey> OP_CHECKSIG
   * The pubkey is either 33 bytes (compressed) or 65 bytes (uncompressed)
   */
  private isP2PKScript(scriptPubKey: string): boolean {
    // P2PK scripts are either:
    // - 67 bytes: 33-byte compressed pubkey + OP_CHECKSIG
    // - 131 bytes: 65-byte uncompressed pubkey + OP_CHECKSIG
    const scriptLength = scriptPubKey.length / 2; // Convert hex length to bytes
    
    if (scriptLength === 35 || scriptLength === 67) {
      // Check if it ends with 'ac' (OP_CHECKSIG)
      return scriptPubKey.endsWith('ac');
    }
    
    return false;
  }

  /**
   * Check if address has any P2PK UTXOs using the new output analyzer
   */
  private async checkForP2PKOutputs(address: string, baseUrl: string): Promise<boolean> {
    try {
      // Import the analyzer dynamically to avoid circular imports
      const { bitcoinOutputAnalyzer } = await import('../crypto/bitcoin-output-analyzer');
      
      // Get first few transactions to check their outputs
      const txResponse = await fetch(`${baseUrl}/address/${address}/txs?limit=5`);
      if (!txResponse.ok) return false;
      
      const txs = await txResponse.json();
      
      // Check outputs in these transactions using the new analyzer
      for (const tx of txs) {
        for (let vout = 0; vout < (tx.vout?.length || 0); vout++) {
          const output = tx.vout[vout];
          if (output?.scriptpubkey_address === address) {
            try {
              // Analyze the specific output using the new analyzer
              const analysis = bitcoinOutputAnalyzer.classifyOutput(output.scriptpubkey, 'mainnet');
              if (analysis.outputType === 'P2PK') {
                return true;
              }
            } catch (error) {
              // If analysis fails, fall back to the old pattern check
              if (this.isP2PKScript(output.scriptpubkey)) {
                return true;
              }
            }
          }
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking for P2PK outputs:', error);
      return false;
    }
  }

  /**
   * Get address information from blockchain
   */
  async getAddressInfo(address: string): Promise<AddressInfo> {
    try {
      const network = this.detectNetwork(address)
      const baseUrl = this.getBaseUrl(network)

      // Get address stats
      const statsResponse = await fetch(`${baseUrl}/address/${address}`)
      if (!statsResponse.ok) {
        throw new Error(`Failed to fetch address info: ${statsResponse.status}`)
      }
      const stats = await statsResponse.json()

      // Use address stats to get accurate counts
      // The stats endpoint provides tx_count, funded_txo_count, spent_txo_count
      const txCount = stats.chain_stats.tx_count
      
      // For sent/received counts, we need to check if any funds have been spent
      const hasSpent = stats.chain_stats.spent_txo_sum > 0
      
      // Debug logging for high-volume addresses
      if (stats.chain_stats.tx_count > 1000) {
        console.log(`Address ${address} full stats:`, {
          chain_stats: stats.chain_stats,
          mempool_stats: stats.mempool_stats,
          hasSpent: hasSpent
        });
      }
      
      // Get accurate transaction counts directly from API stats
      // spent_txo_count = number of transactions where this address was an input (sent)
      // funded_txo_count = number of transactions where this address was an output (received)
      const sentTxCount = stats.chain_stats.spent_txo_count || 0
      const receivedTxCount = stats.chain_stats.funded_txo_count || 0

      // Check if any outputs are P2PK
      const isP2PK = await this.checkForP2PKOutputs(address, baseUrl);

      return {
        address,
        balance: stats.chain_stats.funded_txo_sum - stats.chain_stats.spent_txo_sum,
        totalReceived: stats.chain_stats.funded_txo_sum,
        totalSent: stats.chain_stats.spent_txo_sum,
        txCount: stats.chain_stats.tx_count,
        sentTxCount: sentTxCount, // Estimated from sample
        receivedTxCount: receivedTxCount, // Estimated from sample
        hasTransactions: stats.chain_stats.tx_count > 0,
        hasSpent,
        unconfirmedBalance: stats.mempool_stats.funded_txo_sum - stats.mempool_stats.spent_txo_sum,
        network,
        isP2PK
      }
    } catch (error) {
      console.error('Error fetching address info:', error)
      // Return default values on error
      return {
        address,
        balance: 0,
        totalReceived: 0,
        totalSent: 0,
        txCount: 0,
        hasTransactions: false,
        hasSpent: false,
        unconfirmedBalance: 0,
        network: this.detectNetwork(address)
      }
    }
  }

  /**
   * Get UTXOs for an address
   */
  async getUTXOs(address: string): Promise<UTXO[]> {
    try {
      const network = this.detectNetwork(address)
      const baseUrl = this.getBaseUrl(network)

      const response = await fetch(`${baseUrl}/address/${address}/utxo`)
      if (!response.ok) {
        throw new Error(`Failed to fetch UTXOs: ${response.status}`)
      }

      const utxos = await response.json()
      return utxos.map((utxo: any) => ({
        txid: utxo.txid,
        vout: utxo.vout,
        value: utxo.value,
        status: utxo.status
      }))
    } catch (error) {
      console.error('Error fetching UTXOs:', error)
      return []
    }
  }

  /**
   * Check if an address is valid (basic validation)
   */
  isValidAddress(address: string): boolean {
    // Basic length check
    if (!address || address.length < 26) return false
    
    // P2PKH (Legacy)
    if (address.startsWith('1') && address.length >= 26 && address.length <= 35) return true
    if ((address.startsWith('m') || address.startsWith('n')) && address.length >= 26 && address.length <= 35) return true
    
    // P2SH
    if (address.startsWith('3') && address.length >= 26 && address.length <= 35) return true
    if (address.startsWith('2') && address.length >= 26 && address.length <= 35) return true
    
    // Bech32 (SegWit)
    if ((address.startsWith('bc1') || address.startsWith('tb1') || address.startsWith('bcrt1')) && address.length >= 42) return true
    
    return false
  }

  /**
   * Check for QD3 registration on-chain
   * Scans transactions for OP_RETURN outputs containing QD3 data
   */
  /**
   * Scan an address's transaction history for a QD3 registration record.
   *
   * The API returns transactions newest-first, 25 per page, so a single request
   * only sees the most recent 25. A registration is typically written early in
   * an address's life, which is exactly what a single page misses — so this
   * walks the whole chain via the `/txs/chain/{last_seen_txid}` cursor.
   *
   * A scan that cannot complete reports `scanComplete: false`, and the caller
   * must treat that as "unknown" rather than "unregistered": saying an address
   * is unprotected when it is registered is the costly direction of this error.
   */
  async checkQD3Registration(address: string): Promise<QD3RegistrationStatus> {
    const network = this.detectNetwork(address)
    const baseUrl = this.getBaseUrl(network)

    let lastSeenTxid: string | undefined
    let txsScanned = 0

    try {
      for (let page = 0; page < BlockchainAPI.QD3_SCAN_MAX_PAGES; page++) {
        const url = lastSeenTxid
          ? `${baseUrl}/address/${address}/txs/chain/${lastSeenTxid}`
          : `${baseUrl}/address/${address}/txs`

        const txResponse = await fetch(url)
        if (!txResponse.ok) {
          throw new Error(`Failed to fetch transactions: ${txResponse.status}`)
        }

        const transactions = await txResponse.json()
        if (!Array.isArray(transactions) || transactions.length === 0) {
          // History exhausted — the address genuinely carries no QD3 record.
          return { isRegistered: false, scanComplete: true, txsScanned }
        }

        for (const tx of transactions) {
          txsScanned++
          for (const vout of tx.vout || []) {
            if (vout.scriptpubkey_type === 'op_return' && vout.scriptpubkey) {
              const qd3Data = this.parseQD3Data(vout.scriptpubkey)

              if (qd3Data) {
                return {
                  isRegistered: true,
                  registrationTxId: tx.txid,
                  registrationDate: tx.status?.block_time ? new Date(tx.status.block_time * 1000) : undefined,
                  qd3Version: qd3Data.version,
                  algorithm: qd3Data.algorithm,
                  publicKeyHash: qd3Data.publicKeyHash,
                  signatureHash: qd3Data.signatureHash,
                  rawData: vout.scriptpubkey,
                  scanComplete: true,
                  txsScanned
                }
              }
            }
          }
        }

        lastSeenTxid = transactions[transactions.length - 1]?.txid
        if (!lastSeenTxid) {
          return { isRegistered: false, scanComplete: true, txsScanned }
        }
      }

      // Page cap reached on a long history: not found, but not proven absent.
      return { isRegistered: false, scanComplete: false, txsScanned }
    } catch (error) {
      console.error('Error checking QD3 registration:', error)
      return { isRegistered: false, scanComplete: false, txsScanned }
    }
  }

  /**
   * Parse QD3 data from OP_RETURN hex
   */
  private parseQD3Data(hexData: string): {
    version: number
    algorithm: string
    publicKeyHash: string
    signatureHash: string
  } | null {
    try {
      // Convert hex to bytes
      const bytes = new Uint8Array(hexData.length / 2)
      for (let i = 0; i < hexData.length; i += 2) {
        bytes[i / 2] = parseInt(hexData.substr(i, 2), 16)
      }
      
      // OP_RETURN scripts start with 0x6a (OP_RETURN opcode)
      let offset = 0
      
      // Skip OP_RETURN opcode if present
      if (bytes[0] === 0x6a) offset++
      
      // Skip push data opcode (varies based on data length)
      if (offset < bytes.length) {
        const pushByte = bytes[offset]
        if (pushByte <= 75) {
          // Direct push (1-75 bytes)
          offset++
        } else if (pushByte === 0x4c) {
          // OP_PUSHDATA1
          offset += 2
        } else if (pushByte === 0x4d) {
          // OP_PUSHDATA2
          offset += 3
        }
      }
      
      // Check for QD3 header
      if (bytes.length < offset + 73) return null // Not enough data for QD3
      
      const header = String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2])
      if (header !== 'QD3') return null
      
      // Parse QD3 v2.0 format (73 bytes)
      return {
        version: bytes[offset + 3],
        algorithm: bytes[offset + 4] === 0x01 ? 'ML-DSA-65' : `Unknown(0x${bytes[offset + 4].toString(16)})`,
        publicKeyHash: Array.from(bytes.slice(offset + 5, offset + 37))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(''),
        signatureHash: Array.from(bytes.slice(offset + 37, offset + 69))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('')
      }
    } catch (error) {
      console.error('Error parsing QD3 data:', error)
      return null
    }
  }
}

// Export singleton instance
export const blockchainAPI = BlockchainAPI.getInstance()